import { MethodMarks } from '../method-marks';
import { MarkedProxyDecorator } from '../proxy.decorator';

const marks = new MethodMarks('shared-decorators:rateLimitedMethods');

/**
 * Marks a method as consuming rate-limit budget.
 *
 * The mark is a declaration, not behaviour: it records the method name on the
 * prototype and wraps nothing. The pacing is applied by {@link RateLimiter} at the
 * composition root, which owns the rate itself.
 *
 * Mark the calls that actually reach the downstream service. A method that is async
 * but does no remote work — signing a URL locally, reading a cache — would spend
 * budget against a limit it never touches, so leave it unmarked.
 *
 * **Async only, enforced by the compiler.** Unlike {@link retryable}, which acts only
 * on rejection and so can leave a synchronous method untouched, a limiter must delay
 * *before* invoking. Acquiring budget is therefore unavoidably asynchronous and a
 * marked method always returns a Promise — marking a synchronous one would silently
 * change its signature, so the descriptor type rejects it at compile time.
 *
 * Applies to prototype methods only; an arrow-function class field cannot carry a
 * method decorator.
 *
 * @example
 * class S3FileRepository implements FileRepository {
 *   @rateLimited()
 *   public fetch(path: string): Promise<Buffer> { ... }
 *
 *   // async, but presigns locally — no S3 request, so no budget
 *   public async getSignedURL(path: string): Promise<string> { ... }
 * }
 */
export function rateLimited() {
  // `descriptor` exists to carry the `T` constraint — it is the only place a legacy
  // method decorator can see the method's type. Returned unchanged, which is what
  // returning nothing would do anyway.
  return <T extends (...args: never[]) => Promise<unknown>>(
    prototype: object,
    propertyKey: string | symbol,
    descriptor: TypedPropertyDescriptor<T>
  ): TypedPropertyDescriptor<T> => {
    marks.add(prototype, propertyKey);

    return descriptor;
  };
}

/**
 * The methods marked with {@link rateLimited} on `target` or anywhere up its prototype
 * chain. Empty when nothing is marked.
 *
 * A delegating function rather than `= marks.of`: detaching a method from its instance
 * loses `this`, which would compile and then fail at runtime.
 */
export function rateLimitedMethodsOf(target: object): ReadonlyMap<string, void | undefined> {
  return marks.of(target);
}

/** The rate to hold outbound calls to. */
export interface RateLimitPolicy {
  /** Calls permitted per {@link RateLimitPolicy.perMs}, and the burst capacity. Must be > 0. */
  maxCalls: number;
  /** The window the budget refills over, in milliseconds. Must be > 0. */
  perMs: number;
}

/**
 * RateLimiter
 *
 * A proxy-based decorator that paces a target's {@link rateLimited} methods to
 * `policy.maxCalls` per `policy.perMs`, so we stay inside a downstream service's
 * expected rate instead of discovering it through throttling errors.
 *
 * A token bucket: budget refills continuously and its capacity is `maxCalls`, so an
 * idle period allows a burst up to the full allowance and sustained load settles at
 * the configured rate. A call arriving with no budget waits its turn — callers see
 * only a slower promise — and waiters are released strictly in arrival order.
 *
 * Unmarked methods are returned untouched; the proxy adds no behaviour to them.
 *
 * **The instance is the budget.** Every target it decorates draws on the same bucket,
 * because a provider counts our calls rather than our objects. Use one RateLimiter per
 * downstream service and apply it to every adapter that talks to that service.
 *
 * **Scope is this process.** Across N processes the aggregate is N times the limit, so
 * this paces a single batch or CLI run correctly and cannot enforce a global quota. For
 * that, bound the number of processes — a queue's drain rate or reserved concurrency —
 * rather than reaching for this.
 *
 * **Compose it inside a {@link Retrier}**, so each retry attempt reacquires budget.
 * Inverted, a whole retry sequence would run on one token and a retry storm would
 * bypass the limit exactly when the downstream is already struggling.
 *
 * @example
 * const translating = new ErrorTranslationDecorator(new S3ErrorTranslator());
 * const s3Rate = new RateLimiter({ maxCalls: 100, perMs: 1000 });
 * const retrier = new Retrier({ maxAttempts: 3 });
 *
 * // Both adapters share the same 100 calls/sec
 * const assets = retrier.decorate(s3Rate.decorate(translating.decorate(assetRepository)));
 * const masks = retrier.decorate(s3Rate.decorate(translating.decorate(maskRepository)));
 */
export class RateLimiter extends MarkedProxyDecorator {
  private tokens: number;
  private lastRefillMs: number;
  private readonly waiting: (() => void)[] = [];
  private pumpScheduled = false;

  constructor(private readonly policy: RateLimitPolicy) {
    super(marks);

    // A non-positive or NaN rate would make the refill maths produce Infinity or NaN
    // and hang every caller forever. Fail at construction instead.
    if (!(policy.maxCalls > 0)) throw new Error('[RateLimiter] maxCalls must be greater than 0');
    if (!(policy.perMs > 0)) throw new Error('[RateLimiter] perMs must be greater than 0');

    this.tokens = policy.maxCalls;
    this.lastRefillMs = Date.now();
  }

  protected wrapMarked(method: (...args: unknown[]) => unknown): (...args: unknown[]) => unknown {
    return async (...args: unknown[]) => {
      await this.acquire();

      return method(...args);
    };
  }

  /** Resolves once this call holds a token. Always queues, so order is preserved. */
  private acquire(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.waiting.push(resolve);
      this.pump();
    });
  }

  /**
   * Releases every waiter it has budget for, then schedules itself for when the next
   * token is due. Schedules nothing once the queue drains, so it never holds the event
   * loop open.
   */
  private pump(): void {
    this.pumpScheduled = false;
    this.refill();

    while (this.tokens >= 1) {
      const release = this.waiting.shift();

      if (!release) break;

      this.tokens -= 1;
      release();
    }

    if (this.waiting.length === 0 || this.pumpScheduled) return;

    this.pumpScheduled = true;
    setTimeout(() => this.pump(), this.msUntilNextToken());
  }

  private refill(): void {
    const now = Date.now();
    const elapsedMs = now - this.lastRefillMs;

    if (elapsedMs <= 0) return;

    const perMs = this.policy.maxCalls / this.policy.perMs;

    this.tokens = Math.min(this.policy.maxCalls, this.tokens + elapsedMs * perMs);
    this.lastRefillMs = now;
  }

  private msUntilNextToken(): number {
    const msPerToken = this.policy.perMs / this.policy.maxCalls;

    return Math.max(1, Math.ceil((1 - this.tokens) * msPerToken));
  }
}
